import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";

import { useAuth } from "../contexts/AuthContext";
import useAccessProfile from "../hooks/useAccessProfile";
import {
  isActiveUser,
  hasRole,
  canReviewInspection,
  canManagePickup,
  canManageDelivery,
} from "../utils/accessControl";
import "./InspectionHomePage.css";

/* ------------------------- helpers ------------------------- */

const norm = (v) => String(v ?? "").trim();
const lower = (v) => norm(v).toLowerCase();

const toMillisSafe = (ts) => {
  try {
    if (!ts) return 0;
    if (typeof ts.toMillis === "function") return ts.toMillis();
    if (typeof ts.toDate === "function") return ts.toDate().getTime();
    if (ts instanceof Date) return ts.getTime();
    if (typeof ts === "number") return ts;
    return 0;
  } catch {
    return 0;
  }
};

const stageLabel = (s) => {
  const k = lower(s);
  const MAP = {
    ready_for_inspection: "Ready for Inspection",
    awaiting_dropoff: "Awaiting Drop-off",
    pickup_scheduled: "Pickup Scheduled",
    pickup_in_progress: "Pickup In Progress",
    inspection_passed: "Inspection Passed",
    hub_inspection_pass: "Hub Inspection Passed",
    delivery_scheduled: "Delivery Scheduled",
    delivery_in_progress: "Delivery In Progress",
    completed: "Completed",
    cancelled: "Cancelled",
  };
  return MAP[k] || (k ? k.replace(/_/g, " ") : "—");
};

const formatMoneyZA = (n) => {
  if (typeof n !== "number") return null;
  try {
    return n.toLocaleString("en-ZA", { style: "currency", currency: "ZAR" });
  } catch {
    return `R ${n}`;
  }
};

const getTradeIdFromWorkflow = (wf) =>
  norm(wf?.tradeId || wf?.tradeRefId || wf?.id || "");

const getTradeTitle = (wf) =>
  norm(wf?.tradeTitle || wf?.title || wf?.listingTitle || "");

const getWorkflowStage = (wf) =>
  lower(wf?.stage || "");

const getFulfillmentMethod = (wf) =>
  lower(wf?.fulfillmentChoice?.method || "");

const getDropoffPartnerId = (wf) =>
  norm(wf?.dropoff?.partnerId || "");

const getDropoffPartnerName = (wf) =>
  norm(wf?.dropoff?.partnerName || "");

const getDropoffStoreId = (wf) =>
  norm(
    wf?.dropoff?.storeId ||
      wf?.dropoff?.dropoffPointId ||
      wf?.dropoffPoint?.storeId ||
      wf?.dropoffPoint?.id ||
      ""
  );

const getPaymentStatus = (wf) =>
  lower(wf?.payment?.status || wf?.cash?.status || "");

const getInspectionFeeStatus = (wf) =>
  lower(wf?.inspectionFee?.status || "");

const getAssignedInspectorUid = (wf) =>
  norm(
    wf?.assignedInspectorUid ||
      wf?.inspection?.assignedInspectorUid ||
      wf?.inspectionAssignment?.inspectorUid ||
      wf?.pickup?.assignedInspectorUid ||
      wf?.pickupInspection?.assignedInspectorUid ||
      wf?.hubInspection?.assignedInspectorUid ||
      wf?.dropoff?.assignedInspectorUid ||
      wf?.dropoffIntake?.assignedInspectorUid ||
      ""
  );

const getAssignedInspectorEmail = (wf) =>
  lower(
    wf?.assignedInspectorEmail ||
      wf?.inspection?.assignedInspectorEmail ||
      wf?.inspectionAssignment?.inspectorEmail ||
      wf?.pickup?.assignedInspectorEmail ||
      wf?.pickupInspection?.assignedInspectorEmail ||
      wf?.hubInspection?.assignedInspectorEmail ||
      wf?.dropoff?.assignedInspectorEmail ||
      wf?.dropoffIntake?.assignedInspectorEmail ||
      ""
  );

const getAssignedInspectorName = (wf) =>
  norm(
    wf?.assignedInspectorName ||
      wf?.inspection?.assignedInspectorName ||
      wf?.inspectionAssignment?.inspectorName ||
      wf?.pickup?.assignedInspectorName ||
      wf?.pickupInspection?.assignedInspectorName ||
      wf?.hubInspection?.assignedInspectorName ||
      wf?.dropoff?.assignedInspectorName ||
      wf?.dropoffIntake?.assignedInspectorName ||
      ""
  );

const workflowHasAssignedInspector = (wf) =>
  !!(getAssignedInspectorUid(wf) || getAssignedInspectorEmail(wf));

const workflowAssignedToCurrentUser = (wf, currentUser) => {
  const assignedUid = getAssignedInspectorUid(wf);
  const assignedEmail = getAssignedInspectorEmail(wf);
  const currentUid = norm(currentUser?.uid);
  const currentEmail = lower(currentUser?.email);

  return !!(
    (assignedUid && currentUid && assignedUid === currentUid) ||
    (assignedEmail && currentEmail && assignedEmail === currentEmail)
  );
};

const getAssignedInspectorLabel = (wf) =>
  getAssignedInspectorName(wf) || getAssignedInspectorEmail(wf) || getAssignedInspectorUid(wf);

const queueItemHasAssignedInspector = (item) =>
  !!(item?.hasAssignedInspector || item?.assignedInspectorUid || item?.assignedInspectorEmail);

const getAssignedPartnerId = (wf) =>
  norm(
    wf?.assignedPartnerId ||
      wf?.inspection?.partnerId ||
      wf?.inspectionAssignment?.partnerId ||
      wf?.dropoff?.partnerId ||
      ""
  );

const getAssignedStoreId = (wf) =>
  norm(
    wf?.assignedStoreId ||
      wf?.inspection?.storeId ||
      wf?.inspectionAssignment?.storeId ||
      wf?.dropoff?.storeId ||
      wf?.dropoff?.dropoffPointId ||
      ""
  );

const isPartnerDropoff = (wf) => {
  const stage = getWorkflowStage(wf);
  const method = getFulfillmentMethod(wf);
  const dropoffPartnerId = lower(getDropoffPartnerId(wf));

  return (
    method === "partner_dropoff" ||
    dropoffPartnerId === "approved_dropoff_point" ||
    stage === "awaiting_dropoff"
  );
};

const inferInspectionMode = (wf) => {
  const stage = getWorkflowStage(wf);

  if (stage === "awaiting_dropoff") return "partner_dropoff";
  if (isPartnerDropoff(wf)) return "partner_dropoff";
  if (stage === "pickup_in_progress") return "hub_inspection";
  if (stage === "pickup_scheduled") return "platform_pickup";
  if (stage === "ready_for_inspection") return "platform_in_person";

  return "inspection";
};

const inspectionTaskFromWorkflow = (wf) => {
  const stage = getWorkflowStage(wf);
  const mode = inferInspectionMode(wf);

  if (stage === "awaiting_dropoff") {
    return {
      actionable: true,
      taskKey: "partner_dropoff_intake",
      title: "Drop-off Intake",
      actionLabel: "Open Intake Inspection",
      message: "Inspect the item at the approved drop-off point and record PASS or FAIL.",
      mode,
      priority: "high",
    };
  }

  if (stage === "pickup_scheduled") {
    return {
      actionable: true,
      taskKey: "pickup_inspection",
      title: "Pickup Inspection",
      actionLabel: "Open Pickup Inspection",
      message: "Pickup is scheduled. Open the workflow and complete the inspection during collection.",
      mode,
      priority: "high",
    };
  }

  if (stage === "pickup_in_progress") {
    return {
      actionable: true,
      taskKey: "hub_inspection",
      title: "Hub Inspection",
      actionLabel: "Open Hub Inspection",
      message: "Pickup is in progress. Continue with the post-custody inspection step.",
      mode,
      priority: "medium",
    };
  }

  if (stage === "ready_for_inspection") {
    return {
      actionable: true,
      taskKey: "inspection_ready",
      title: "Inspection Ready",
      actionLabel: "Open Inspection Workflow",
      message: "This workflow is ready for the next inspection step.",
      mode,
      priority: "medium",
    };
  }

  return null;
};

const workflowMatchesSearch = (wf, q) => {
  const search = lower(q);
  if (!search) return true;

  const hay = lower(
    [
      getTradeIdFromWorkflow(wf),
      getTradeTitle(wf),
      getWorkflowStage(wf),
      getFulfillmentMethod(wf),
      getDropoffPartnerName(wf),
      getDropoffPartnerId(wf),
      getDropoffStoreId(wf),
      getAssignedInspectorUid(wf),
      getAssignedInspectorEmail(wf),
      getAssignedInspectorName(wf),
      getAssignedPartnerId(wf),
      getAssignedStoreId(wf),
      getPaymentStatus(wf),
      getInspectionFeeStatus(wf),
      inferInspectionMode(wf),
    ].join(" ")
  );

  return hay.includes(search);
};

const canSeeInspectionPortal = (profile) => {
  if (!isActiveUser(profile)) return false;

  return (
    hasRole(profile, "admin") ||
    hasRole(profile, "inspector") ||
    hasRole(profile, "partner_inspector") ||
    canReviewInspection(profile) ||
    canManagePickup(profile) ||
    canManageDelivery(profile)
  );
};

const isPrivilegedInspectionUser = (profile) => {
  return (
    hasRole(profile, "admin") ||
    canManagePickup(profile) ||
    canManageDelivery(profile)
  );
};

const canAssignInspectionFromQueue = (profile) => {
  return isActiveUser(profile) && hasRole(profile, "admin");
};

const isAssignableInspectorProfile = (p) => {
  if (!isActiveUser(p)) return false;

  // Assignment list must contain only normal inspector users.
  // Admins and partner inspectors are excluded from this selector.
  return (
    hasRole(p, "inspector") &&
    !hasRole(p, "admin") &&
    !hasRole(p, "partner_inspector")
  );
};

const getInspectorDisplayLabel = (inspector) => {
  const name = norm(inspector?.displayName || inspector?.name);
  const email = lower(inspector?.email);
  const type = lower(inspector?.staffProfile?.inspectorType);

  const roleLabel =
    type === "partner"
      ? "Partner"
      : hasRole(inspector, "partner_inspector")
      ? "Partner"
      : "Internal";

  if (name && email) return `${name} • ${email} • ${roleLabel}`;
  if (email) return `${email} • ${roleLabel}`;
  return `${inspector?.uid || "Inspector"} • ${roleLabel}`;
};

const isPartnerInspectorUser = (profile) => {
  return (
    hasRole(profile, "partner_inspector") ||
    lower(profile?.staffProfile?.inspectorType) === "partner"
  );
};

const isInternalInspectorUser = (profile) => {
  return (
    hasRole(profile, "inspector") ||
    lower(profile?.staffProfile?.inspectorType) === "internal" ||
    canReviewInspection(profile)
  );
};

const workflowVisibleToInspector = (wf, currentUser, profile, showMineOnly) => {
  const hasAssignee = workflowHasAssignedInspector(wf);
  const assignedToMe = workflowAssignedToCurrentUser(wf, currentUser);

  const userPartnerId = norm(profile?.staffProfile?.partnerId);
  const userStoreId = norm(profile?.staffProfile?.storeId);

  const wfPartnerId = norm(getAssignedPartnerId(wf) || getDropoffPartnerId(wf));
  const wfStoreId = norm(getAssignedStoreId(wf) || getDropoffStoreId(wf));

  // If a task is assigned to someone specific, only that person sees it in
  // "My scope". Admin / operations users can still see it in "All visible".
  if (hasAssignee) {
    if (assignedToMe) return true;
    return isPrivilegedInspectionUser(profile) && !showMineOnly;
  }

  // Partner inspectors see unassigned work only for their configured partner/store.
  // If the partner profile is incomplete, we do not expose partner work.
  if (isPartnerInspectorUser(profile)) {
    if (!userPartnerId && !userStoreId) return false;

    if (userPartnerId && wfPartnerId && userPartnerId !== wfPartnerId) return false;
    if (userStoreId && wfStoreId && userStoreId !== wfStoreId) return false;

    return true;
  }

  // Internal inspectors should see open inspection work even before an admin
  // explicitly assigns the task. This closes the role-vs-assignment gap.
  if (isInternalInspectorUser(profile)) {
    return true;
  }

  return isPrivilegedInspectionUser(profile) && !showMineOnly;
};
const formatModeLabel = (mode) => {
  const m = lower(mode);
  if (m === "partner_dropoff") return "Partner Drop-off";
  if (m === "platform_pickup") return "Platform Pickup";
  if (m === "hub_inspection") return "Hub Inspection";
  if (m === "platform_in_person") return "In-person";
  return m ? m.replace(/_/g, " ") : "—";
};

const formatStatusLabel = (status) => {
  const s = lower(status);
  if (!s) return "—";
  return s.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
};

const formatInspectorType = (profile) => {
  const t = lower(profile?.staffProfile?.inspectorType);
  if (t === "partner") return "Partner Inspector";
  if (t === "internal") return "Internal Inspector";
  if (hasRole(profile, "admin")) return "Admin";
  if (hasRole(profile, "inspector")) return "Inspector";
  if (hasRole(profile, "partner_inspector")) return "Partner Inspector";
  return "Inspection Access";
};

const getUrgencyLabel = (row) => {
  if (row.task?.priority === "high") return "Priority";
  if (!row.assignedInspectorUid) return "Open";
  return "Normal";
};

const getUrgencyClass = (row) => {
  if (row.task?.priority === "high") return "iph-badge--priority";
  if (!row.assignedInspectorUid) return "iph-badge--open";
  return "iph-badge--assigned";
};

const groupQueueItems = (items) => {
  const groups = {
    priority: [],
    assigned: [],
    open: [],
  };

  items.forEach((item) => {
    if (item.task?.priority === "high") {
      groups.priority.push(item);
      return;
    }
    if (queueItemHasAssignedInspector(item)) {
      groups.assigned.push(item);
      return;
    }
    groups.open.push(item);
  });

  return groups;
};

const formatElapsed = (ms) => {
    if (!ms) return "Unknown";
    const diff = Math.max(0, Date.now() - ms);
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m waiting`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h waiting`;
    const days = Math.floor(hrs / 24);
    return `${days}d waiting`;
};

const getShortId = (id) => {
  const v = norm(id);
  if (!v) return "—";
  if (v.length <= 14) return v;
  return `${v.slice(0, 6)}…${v.slice(-5)}`;
};

const getWaitingHours = (ms) => {
  if (!ms) return 0;
  return Math.floor(Math.max(0, Date.now() - ms) / 3600000);
};

const isOverdueQueueItem = (item) => {
  const hours = getWaitingHours(item?.updatedMs);

  // High-priority tasks should be visible sooner.
  if (item?.task?.priority === "high" && hours >= 12) return true;

  // Normal inspection work becomes overdue after 24h.
  return hours >= 24;
};

const getSlaTone = (item) => {
  if (isOverdueQueueItem(item)) return "overdue";
  if (item?.task?.priority === "high") return "priority";
  if (!queueItemHasAssignedInspector(item)) return "open";
  return "normal";
};

const getSlaLabel = (item) => {
  if (isOverdueQueueItem(item)) return "Overdue";
  if (item?.task?.priority === "high") return "Priority";
  if (!queueItemHasAssignedInspector(item)) return "Open";
  return "Assigned";
};

  /* ------------------------- component ------------------------- */

export default function InspectionHomePage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { profile, loading: accessLoading, exists: accessExists } = useAccessProfile();

  const [queueLoading, setQueueLoading] = useState(true);
  const [queueItems, setQueueItems] = useState([]);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [showMineOnly, setShowMineOnly] = useState(true);
  const [sortBy, setSortBy] = useState("recent");
  const [quickFilter, setQuickFilter] = useState("all");
  const [freshTaskIds, setFreshTaskIds] = useState([]);
  const [expandedTaskId, setExpandedTaskId] = useState("");

  const [inspectors, setInspectors] = useState([]);
  const [inspectorsLoading, setInspectorsLoading] = useState(false);
  const [assignModalItem, setAssignModalItem] = useState(null);
  const [selectedInspectorUid, setSelectedInspectorUid] = useState("");
  const [assignBusy, setAssignBusy] = useState(false);
  const [assignError, setAssignError] = useState("");

  const unsubRef = useRef(null);
  const didSetDefaultScopeRef = useRef(false);

  const portalAllowed = useMemo(() => canSeeInspectionPortal(profile), [profile]);


  const canAssignFromQueue = useMemo(
    () => canAssignInspectionFromQueue(profile),
    [profile]
  );

  const assignInspectionCallable = useMemo(
    () => httpsCallable(functions, "assignInspectionToInspector"),
    []
  );

  useEffect(() => {
    if (didSetDefaultScopeRef.current || accessLoading || !profile) return;
    if (!isActiveUser(profile)) return;

    // Admin / operations users should land on the full operational queue.
    // Inspectors land on their scoped queue.
    setShowMineOnly(!isPrivilegedInspectionUser(profile));
    didSetDefaultScopeRef.current = true;
  }, [accessLoading, profile]);

 useEffect(() => {
    if (!canAssignFromQueue) {
      setInspectors([]);
      setInspectorsLoading(false);
      return;
    }

    let cancelled = false;

    const loadAssignableInspectors = async () => {
      setInspectorsLoading(true);

      try {
        const snap = await db
          .collection("users")
          .where("roles", "array-contains", "inspector")
          .limit(300)
          .get();

        if (cancelled) return;

        const list = snap.docs
          .map((d) => {
            const data = d.data() || {};

            return {
              id: d.id,
              ...data,
              uid: norm(data.uid) || d.id,
              email: data.email || data.emailLower || "",
              roles: Array.isArray(data.roles) ? data.roles : [],
              permissions:
                data.permissions && typeof data.permissions === "object"
                  ? data.permissions
                  : {},
              staffProfile:
                data.staffProfile && typeof data.staffProfile === "object"
                  ? data.staffProfile
                  : {},
              isActive: data.isActive !== false,
            };
          })
          .filter(isAssignableInspectorProfile)
          .sort((a, b) =>
            getInspectorDisplayLabel(a).localeCompare(getInspectorDisplayLabel(b))
          );

        setInspectors(list);
      } catch (err) {
        console.error("Could not load inspectors:", err);
        setInspectors([]);
      } finally {
        if (!cancelled) setInspectorsLoading(false);
      }
    };

    loadAssignableInspectors();

    return () => {
      cancelled = true;
    };
  }, [canAssignFromQueue]);

  const queueSummary = useMemo(() => {
    return {
      total: queueItems.length,
      assignedToMe: queueItems.filter((item) => item.assignedToCurrentUser).length,
      unassigned: queueItems.filter((item) => !queueItemHasAssignedInspector(item)).length,
      partnerDropoff: queueItems.filter((item) => item.task?.mode === "partner_dropoff").length,
    };
  }, [queueItems]);

  const attachInspectionQueue = useCallback(() => {
    if (!portalAllowed || !currentUser?.uid) {
      setQueueItems([]);
      setQueueLoading(false);
      return;
    }

    if (unsubRef.current) {
      try {
        unsubRef.current();
      } catch {}
      unsubRef.current = null;
    }

    setQueueLoading(true);

    const q = db
      .collection("TradeWorkflows")
      .orderBy("updatedAt", "desc")
      .limit(500);

    const unsub = q.onSnapshot(
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        const filtered = rows
          .map((wf) => {
            const task = inspectionTaskFromWorkflow(wf);
            if (!task) return null;

            const tradeId = getTradeIdFromWorkflow(wf) || wf.id;
            const stage = getWorkflowStage(wf);

            return {
              id: wf.id,
              wf,
              tradeId,
              stage,
              title: getTradeTitle(wf) || task.title || tradeId,
              task,
              assignedInspectorUid: getAssignedInspectorUid(wf),
              assignedInspectorEmail: getAssignedInspectorEmail(wf),
              assignedInspectorName: getAssignedInspectorName(wf),
              assignedInspectorLabel: getAssignedInspectorLabel(wf),
              assignedToCurrentUser: workflowAssignedToCurrentUser(wf, currentUser),
              hasAssignedInspector: workflowHasAssignedInspector(wf),
              assignedPartnerId: getAssignedPartnerId(wf),
              assignedStoreId: getAssignedStoreId(wf),
              updatedMs: toMillisSafe(wf.updatedAt || wf.lastUpdatedAt || wf.createdAt),
            };
          })
          .filter(Boolean)
          .filter((row) => {
            if (stageFilter !== "all" && row.stage !== stageFilter) return false;
            if (!workflowMatchesSearch(row.wf, search)) return false;

            if (quickFilter === "priority" && row.task?.priority !== "high") return false;
            if (quickFilter === "assigned" && !queueItemHasAssignedInspector(row)) return false;
            if (quickFilter === "open" && queueItemHasAssignedInspector(row)) return false;
            if (quickFilter === "partner_dropoff" && row.task?.mode !== "partner_dropoff") return false;
            if (quickFilter === "pickup_scheduled" && row.stage !== "pickup_scheduled") return false;

            return workflowVisibleToInspector(
              row.wf,
              currentUser,
              profile,
              showMineOnly
            );
          })
          .sort((a, b) => {
            if (sortBy === "priority") {
              const pa = a.task?.priority === "high" ? 1 : 0;
              const pb = b.task?.priority === "high" ? 1 : 0;
              if (pb !== pa) return pb - pa;
            }
            return (b.updatedMs || 0) - (a.updatedMs || 0);
          });

        setQueueItems((prev) => {
          const prevIds = new Set((prev || []).map((x) => x.id));
          const newIds = filtered.map((x) => x.id).filter((id) => !prevIds.has(id));

          if (newIds.length) {
            setFreshTaskIds((curr) => Array.from(new Set([...curr, ...newIds])));
            setTimeout(() => {
              setFreshTaskIds((curr) => curr.filter((id) => !newIds.includes(id)));
            }, 6000);
          }

          return filtered;
        });
        setQueueLoading(false);
      },
      (err) => {
        console.error("Inspection queue snapshot error:", err);
        setQueueItems([]);
        setQueueLoading(false);
      }
    );

    unsubRef.current = unsub;
  }, [portalAllowed, currentUser, profile, search, stageFilter, showMineOnly, sortBy, quickFilter]);

  useEffect(() => {
    attachInspectionQueue();

    return () => {
      if (unsubRef.current) {
        try {
          unsubRef.current();
        } catch {}
        unsubRef.current = null;
      }
    };
  }, [attachInspectionQueue]);

  const nextBestTask = useMemo(() => {
    if (!queueItems.length) return null;

    const scored = [...queueItems].map((item) => {
      let score = 0;

      if (item.task?.priority === "high") score += 100;
      if (item.assignedToCurrentUser) score += 40;
      if (!queueItemHasAssignedInspector(item)) score += 20;
      if (item.stage === "awaiting_dropoff") score += 15;
      if (item.stage === "pickup_scheduled") score += 12;
      score += Math.min(Math.floor((item.updatedMs || 0) / 1000000), 10);

      return { ...item, score };
    });

    scored.sort((a, b) => b.score - a.score);
    return scored[0] || null;
  }, [queueItems, currentUser]);

   const compactQueueView = useMemo(() => {
    const overdue = [];
    const current = [];

    queueItems.forEach((item) => {
      if (isOverdueQueueItem(item)) overdue.push(item);
      else current.push(item);
    });

    return {
      overdue,
      current,
      groupedCurrent: groupQueueItems(current),
    };
  }, [queueItems]);

  if (accessLoading) {
    return (
      <div className="iph-shell">
        <div className="iph-container">
          <div className="iph-empty-state">Loading inspection access…</div>
        </div>
      </div>
    );
  }

  if (!accessExists) {
    return (
      <div className="iph-shell">
        <div className="iph-container">
          <div className="iph-empty-state">
            ⛔ Access profile not provisioned. Please contact a super admin.
          </div>
        </div>
      </div>
    );
  }

  if (!isActiveUser(profile)) {
    return (
      <div className="iph-shell">
        <div className="iph-container">
          <div className="iph-empty-state">
            ⛔ Your account is inactive. Please contact an administrator.
          </div>
        </div>
      </div>
    );
  }

  if (!portalAllowed) {
    return (
      <div className="iph-shell">
        <div className="iph-container">
          <div className="iph-empty-state">
            ⛔ You do not have permission to access the inspection portal.
          </div>
        </div>
      </div>
    );
  }

const openWorkflow = (tradeId) => {
  if (!tradeId) return;
  navigate(`/trade/${encodeURIComponent(tradeId)}?tab=workflow`);
};

const openAssignInspectorModal = (item) => {
  if (!canAssignFromQueue || !item?.id) return;

  const existingUid = norm(item.assignedInspectorUid);
  const existingEmail = lower(item.assignedInspectorEmail);

  const matchedInspector = inspectors.find((inspector) => {
    const uidMatch = existingUid && inspector.uid === existingUid;
    const emailMatch = existingEmail && lower(inspector.email) === existingEmail;
    return uidMatch || emailMatch;
  });

  setAssignError("");
  setAssignModalItem(item);
  setSelectedInspectorUid(matchedInspector?.uid || existingUid || "");
};

const closeAssignInspectorModal = () => {
  if (assignBusy) return;

  setAssignModalItem(null);
  setSelectedInspectorUid("");
  setAssignError("");
};

const handleAssignInspection = async () => {
  if (!assignModalItem?.id) {
    setAssignError("Missing workflow ID.");
    return;
  }

  if (!selectedInspectorUid) {
    setAssignError("Select an inspector first.");
    return;
  }

  const inspector = inspectors.find((x) => x.uid === selectedInspectorUid);

  if (!inspector) {
    setAssignError("Selected inspector could not be found.");
    return;
  }

  setAssignBusy(true);
  setAssignError("");

  try {
    await assignInspectionCallable({
      workflowId: assignModalItem.id,
      inspectorUid: inspector.uid,
      inspectorEmail: inspector.email || "",
      inspectorName:
        inspector.displayName ||
        inspector.name ||
        inspector.email ||
        inspector.uid,

      partnerId: inspector.staffProfile?.partnerId || "",
      storeId: inspector.staffProfile?.storeId || "",
      note: "Assigned from inspection queue",
    });

    setAssignModalItem(null);
    setSelectedInspectorUid("");
    setAssignError("");

    attachInspectionQueue();
  } catch (err) {
    console.error("Could not assign inspection:", err);
    setAssignError(
      err?.message ||
        "Could not assign this inspection. Please check permissions and try again."
    );
  } finally {
    setAssignBusy(false);
  }
};

const renderTaskRow = (item, options = {}) => {
  const { urgent = false } = options;

  const {
    wf,
    tradeId,
    stage,
    title: itemTitle,
    task,
    assignedInspectorUid,
    assignedInspectorEmail,
    assignedInspectorLabel,
    updatedMs,
  } = item;

  const isExpanded = expandedTaskId === item.id;
  const paymentStatus = getPaymentStatus(wf);
  const inspectionFeeStatus = getInspectionFeeStatus(wf);
  const partnerName = getDropoffPartnerName(wf);
  const storeId = getDropoffStoreId(wf);

  const amount =
    typeof wf?.amount === "number"
      ? formatMoneyZA(wf.amount)
      : typeof wf?.cashAmount === "number"
      ? formatMoneyZA(wf.cashAmount)
      : typeof wf?.cash?.amountGross === "number"
      ? formatMoneyZA(wf.cash.amountGross)
      : null;

  const slaTone = getSlaTone(item);
  const hasAssigned = queueItemHasAssignedInspector(item);

  return (
    <article
      key={item.id}
      className={[
        "iph-task-row",
        urgent ? "iph-task-row--urgent" : "",
        freshTaskIds.includes(item.id) ? "iph-task-row--fresh" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="iph-task-left">
        <div className={`iph-task-dot iph-task-dot--${slaTone}`} />

        <div className="iph-task-content">
          <div className="iph-task-kicker">
            <span>{task?.title || "Inspection Task"}</span>
            <span>•</span>
            <span>{formatModeLabel(task?.mode)}</span>
            {updatedMs ? (
              <>
                <span>•</span>
                <span>{formatElapsed(updatedMs)}</span>
              </>
            ) : null}
          </div>

          <h3 className="iph-task-title">
            {itemTitle || getShortId(tradeId) || "Inspection task"}
          </h3>

          <div className="iph-task-subline">
            <span>Trade {getShortId(tradeId)}</span>
            <span>•</span>
            <span>{stageLabel(stage)}</span>
            {partnerName ? (
              <>
                <span>•</span>
                <span>{partnerName}</span>
              </>
            ) : null}
          </div>
        </div>
      </div>

      <div className="iph-task-mid">
        <span className={`iph-sla-pill iph-sla-pill--${slaTone}`}>
          {getSlaLabel(item)}
        </span>

        {hasAssigned ? (
          <span className="iph-mini-pill">Assigned</span>
        ) : (
          <span className="iph-mini-pill iph-mini-pill--open">Unassigned</span>
        )}

        {amount ? <span className="iph-mini-pill">{amount}</span> : null}
      </div>

      <div className="iph-task-actions-compact">
        <button
          type="button"
          className="iph-btn iph-btn-primary iph-btn-sm"
          onClick={() => openWorkflow(tradeId)}
        >
          Open
        </button>

        <button
          type="button"
          className="iph-icon-action"
          onClick={() =>
            setExpandedTaskId((prev) => (prev === item.id ? "" : item.id))
          }
          aria-expanded={isExpanded}
        >
          {isExpanded ? "Hide" : "Details"}
        </button>

        {canAssignFromQueue ? (
          <button
            type="button"
            className="iph-icon-action iph-icon-action--assign"
            onClick={() => openAssignInspectorModal(item)}
          >
            {hasAssigned ? "Reassign" : "Assign"}
          </button>
        ) : null}
      </div>

      {isExpanded ? (
        <div className="iph-task-expanded">
          <div className="iph-expanded-note">{task?.message}</div>

          <div className="iph-expanded-grid">
            <div>
              <span>Trade ID</span>
              <strong>{tradeId || "—"}</strong>
            </div>

            <div>
              <span>Inspector</span>
              <strong>{assignedInspectorLabel || assignedInspectorEmail || assignedInspectorUid || "Not assigned"}</strong>
            </div>

            <div>
              <span>Payment</span>
              <strong>{paymentStatus ? formatStatusLabel(paymentStatus) : "—"}</strong>
            </div>

            <div>
              <span>Inspection Fee</span>
              <strong>
                {inspectionFeeStatus ? formatStatusLabel(inspectionFeeStatus) : "—"}
              </strong>
            </div>

            {partnerName ? (
              <div>
                <span>Drop-off Point</span>
                <strong>{partnerName}</strong>
              </div>
            ) : null}

            {storeId ? (
              <div>
                <span>Store ID</span>
                <strong>{storeId}</strong>
              </div>
            ) : null}

            {updatedMs ? (
              <div>
                <span>Last Updated</span>
                <strong>{new Date(updatedMs).toLocaleString()}</strong>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </article>
  );
};

const renderQueueSection = (title, subtitle, items, options = {}) => {
    if (!items.length) return null;

    return (
      <section
        className={[
          "iph-compact-section",
          options.urgent ? "iph-compact-section--urgent" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        key={title}
      >
        <div className="iph-compact-section-head">
          <div>
            <div className="iph-section-eyebrow">{subtitle}</div>
            <h2>{title}</h2>
          </div>

          <span className="iph-section-count-compact">{items.length}</span>
        </div>

        <div className="iph-task-list">
          {items.map((item) => renderTaskRow(item, { urgent: options.urgent }))}
        </div>
      </section>
    );
  };

  return (
    <div className="iph-shell iph-shell--compact">
      <div className="iph-container iph-container--compact">
        <section className="iph-command-bar">
          <div className="iph-command-main">
            <div className="iph-command-eyebrow">
              Inspection Operations
              <span>{formatInspectorType(profile)}</span>
            </div>

            <h1>Inspection Queue</h1>

            <p>
              A cleaner queue view for intake, pickup, and hub inspection tasks.
            </p>
          </div>

          <div className="iph-command-actions">
            <div className="iph-operator-pill">
              <span className="iph-status-dot" />
              <span>{currentUser?.email || "Signed in"}</span>
            </div>

            <button
              type="button"
              className="iph-btn iph-btn-secondary"
              onClick={() => navigate("/")}
            >
              Home
            </button>

            <button
              type="button"
              className="iph-btn iph-btn-primary"
              onClick={attachInspectionQueue}
              disabled={queueLoading}
            >
              {queueLoading ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </section>

        <section className="iph-mini-metrics">
          <article className="iph-mini-metric iph-mini-metric--active">
            <span>Visible</span>
            <strong>{queueSummary.total}</strong>
          </article>

          <article className="iph-mini-metric">
            <span>Mine</span>
            <strong>{queueSummary.assignedToMe}</strong>
          </article>

          <article className="iph-mini-metric">
            <span>Open</span>
            <strong>{queueSummary.unassigned}</strong>
          </article>

          <article className="iph-mini-metric">
            <span>Drop-off</span>
            <strong>{queueSummary.partnerDropoff}</strong>
          </article>
        </section>

        <section className="iph-control-panel">
          <label className="iph-filter iph-filter--grow">
            <span>Search</span>
            <input
              type="text"
              placeholder="Trade ID, title, partner, store, stage…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>

          <label className="iph-filter">
            <span>Stage</span>
            <select
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value)}
            >
              <option value="all">All stages</option>
              <option value="ready_for_inspection">Ready for inspection</option>
              <option value="awaiting_dropoff">Awaiting drop-off</option>
              <option value="pickup_scheduled">Pickup scheduled</option>
              <option value="pickup_in_progress">Pickup in progress</option>
            </select>
          </label>

          <label className="iph-filter">
            <span>Sort</span>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="recent">Most recent</option>
              <option value="priority">Priority first</option>
            </select>
          </label>

          <label className="iph-toggle iph-toggle--compact">
            <input
              type="checkbox"
              checked={showMineOnly}
              onChange={(e) => setShowMineOnly(e.target.checked)}
            />
            <span>{showMineOnly ? "My scope" : "All visible"}</span>
          </label>
        </section>

        <section className="iph-chip-row">
          {[
            ["all", "All"],
            ["priority", "Priority"],
            ["assigned", "Assigned"],
            ["open", "Open"],
            ["partner_dropoff", "Partner Drop-off"],
            ["pickup_scheduled", "Pickup Scheduled"],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={`iph-chip ${quickFilter === value ? "is-active" : ""}`}
              onClick={() => setQuickFilter(value)}
            >
              {label}
            </button>
          ))}
        </section>

        {nextBestTask ? (
          <section className="iph-next-action">
            <div>
              <div className="iph-section-eyebrow">Recommended next action</div>
              <h2>{nextBestTask.title || nextBestTask.tradeId || "Next task"}</h2>
              <p>{nextBestTask.task?.message}</p>
            </div>

            <div className="iph-next-action-right">
              <span className={`iph-sla-pill iph-sla-pill--${getSlaTone(nextBestTask)}`}>
                {getSlaLabel(nextBestTask)}
              </span>

              <button
                type="button"
                className="iph-btn iph-btn-primary"
                onClick={() => openWorkflow(nextBestTask.tradeId)}
              >
                {nextBestTask.task?.actionLabel || "Open Task"}
              </button>
            </div>
          </section>
        ) : null}

        {queueLoading ? (
          <div className="iph-empty-state iph-empty-state--compact">
            Loading inspection queue…
          </div>
        ) : queueItems.length === 0 ? (
          <div className="iph-empty-state iph-empty-state--compact">
            No inspection tasks are visible right now.
          </div>
        ) : (
          <main className="iph-queue-stack">
            {renderQueueSection(
              "Overdue",
              "Needs attention",
              compactQueueView.overdue,
              { urgent: true }
            )}

            {renderQueueSection(
              "Priority Work",
              "Act first",
              compactQueueView.groupedCurrent.priority
            )}

            {renderQueueSection(
              "Assigned Work",
              "Already allocated",
              compactQueueView.groupedCurrent.assigned
            )}

            {renderQueueSection(
              "Open Work",
              "Available tasks",
              compactQueueView.groupedCurrent.open
            )}
          </main>
        )}
            </div>

      {assignModalItem && canAssignFromQueue ? (
        <div className="iph-modal-backdrop" role="dialog" aria-modal="true">
          <div className="iph-assign-modal">
            <div className="iph-assign-modal-head">
              <div>
                <div className="iph-section-eyebrow">Assign inspection</div>
                <h2>{assignModalItem.title || "Inspection task"}</h2>
                <p>
                  Trade {getShortId(assignModalItem.tradeId)} •{" "}
                  {stageLabel(assignModalItem.stage)}
                </p>
              </div>

              <button
                type="button"
                className="iph-modal-close"
                onClick={closeAssignInspectorModal}
                disabled={assignBusy}
                aria-label="Close assignment modal"
              >
                ×
              </button>
            </div>

            <label className="iph-filter iph-assign-select">
              <span>Inspector</span>
              <select
                value={selectedInspectorUid}
                onChange={(e) => {
                  setSelectedInspectorUid(e.target.value);
                  setAssignError("");
                }}
                disabled={assignBusy || inspectorsLoading}
              >
                <option value="">
                  {inspectorsLoading
                    ? "Loading inspectors..."
                    : inspectors.length
                    ? "Select inspector"
                    : "No inspector users found"}
                </option>

                {inspectors.map((inspector) => (
                  <option key={inspector.uid} value={inspector.uid}>
                    {getInspectorDisplayLabel(inspector)}
                  </option>
                ))}
              </select>
            </label>

            {assignModalItem.assignedInspectorLabel ? (
              <div className="iph-assign-current">
                Current inspector:{" "}
                <strong>{assignModalItem.assignedInspectorLabel}</strong>
              </div>
            ) : (
              <div className="iph-assign-current">
                This inspection is currently unassigned.
              </div>
            )}

            {assignError ? (
              <div className="iph-assign-error">{assignError}</div>
            ) : null}

            <div className="iph-assign-actions">
              <button
                type="button"
                className="iph-btn iph-btn-secondary"
                onClick={closeAssignInspectorModal}
                disabled={assignBusy}
              >
                Cancel
              </button>

              <button
                type="button"
                className="iph-btn iph-btn-primary"
                onClick={handleAssignInspection}
                disabled={assignBusy || inspectorsLoading || !selectedInspectorUid}
              >
                {assignBusy ? "Assigning..." : "Assign inspector"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}